import React from "react";
import { Section } from "../../../components/section";
import { SectionHeader } from "../../../components/section-header";
import { ImageTextTriplets } from "../../../components/image-text-triplets";
import { SolutionHeader } from "../../../components/solution-header";
import { MethodOverview } from "../../../components/method-overview";
import { FullWidthImage } from "../../../components/full-width-image";
import { ParagraphGroup } from "../../../components/paragraph-group";
import actionlevels from "../../../images/Design_LevelsofAction.png";
import S3EH from "../../../images/Design_EngagementHubS3.png";
import S3FC from "../../../images/Design_FactcheckerS3.png";
import S3FCS from "../../../images/Design_Sources.png";
import S3C from "../../../images/Design_CorrectionsS3.png";
import S3A from "../../../images/Design_AccountsS3.png";
import CCV from "../../../images/Design_3CCTests.png";
import exceptions from "../../../images/Design_Exceptions.png";
import { ScreenGroup } from "../../../components/screen-group";
import { ImageTextPair } from "../../../components/image-text-pair";
import { Insight } from "../../../components/insight";

export interface IConsolidationProps {
  sectionId: string;
  index: number;
}

export function Refinement(props: IConsolidationProps) {
  const formattedIndex = props.index > 10 ? props.index : `0${props.index}`;
  return (
    <Section sectionId={props.sectionId} isLast={false}>
      <SectionHeader title={`${formattedIndex} Refinement`} />

      <SolutionHeader
        title="3 Levels of Action"
        description="As we gathered more insights about actionability from user research, we realized that our target audience had varying tendencies for action within our prototype that we needed to design for.  We classified these action levels as low, medium and high and categorized our features according to how they appealed to each."
      />

      <FullWidthImage imgSrc={actionlevels} />

      <ImageTextTriplets
        children={[
          {
            title: "Low",
            body:
              "Prefers quick, sweeping actions against misinformation that require minimal time and effort",
          },
          {
            title: "Medium",
            body:
              "Prefers more granular control; will put effort into evaluating and managing their engagement with misinformation when the content is especially important to them",
          },
          {
            title: "High",
            body:
              "Spends the largest amount of effort evaluating information and wants the most granular level of control in managing their engagement with misinformation",
          },
        ]}
      />

      <MethodOverview
        methods={[
          {
            title: "Fact-Checker",
            body:
              "In addition to refining for usability, we created the How We Fact Check module to answer the recurring questions our participants asked in testing: Who is doing the fact-checking? How is credibility being evaluated? Participants wanted to know which pieces of information were backed by which sources, and they also needed to know more about how credibility was being evaluated in order to trust the fact-checker.",
            imgSrc: S3FC,
          },
          {
            title: "Fact-Check Sources",
            body:
              "We saw in testing that users trusted the fact-checker more when it cited sources that they trusted, so we created this feature in the Engagement Hub allowed users to set their preferred sources. This does not affect fact-check verdicts, but fact-check detail pages would provide links to these sources when available. Users would then be able to see when their preferred sources support a verdict and they can learn more about topics from the sources they like.",
            imgSrc: S3FCS,
          },

          {
            title: "Engagement Hub",
            body:
              "To optimize the Engagement Hub in supporting user goals and actions, we restructured the features within the Hub to bring forward what our participants felt were the most actionable elements and account for the different levels of action they wanted to take.",
            imgSrc: S3EH,
          },
        ]}
      />

      <ScreenGroup
        title="Quick Actions"
        description="In this iteration, to address our previous issues with actionability, we surfaced the actions that would require the most immediate attention to the top of the Hub—users would also receive push notifications that brought them to these actions. Through testing, we identified these as the features that users would want to take the most immediate action on."
        features={[
          {
            title: "Corrections",
            body:
              "In this iteration, we focused on making usability changes, such as restructuring the page to appear as a feed, and highlighted key information that was important to users.",
            imgSrc: S3C,
          },
        ]}
      />
      <Insight
        insight={{
          body:
            "96% of users wanted to remove or disengage from posts that were verified as containing misinformation.",
        }}
        index={0}
      />
      <ImageTextPair
        imgSrc={S3A}
        title="Accounts to Review"
        body="Most users wanted to take action at the account level, so we created this new page to allow users to quickly identify and unfollow accounts responsible for a high proportion of misinformation within their engagement."
        goofy={true}
      />
      <Insight
        insight={{
          body:
            "In testing, 89% of participants removed engagements with high-risk accounts.",
        }}
        index={1}
      />

      <SolutionHeader
        title="Consumption and Creation Trends"
        description="In refining the data visualizations, the main challenge was determining the right level of detail to display, and what provided the most relevant information to users. To address this, we A/B tested three different visualizations."
      />

      <FullWidthImage imgSrc={CCV} />

      <ParagraphGroup
        body={[
          "Users overwhelmingly preferred the bar graph version, given that they were already familiar with the format in other apps they used, such as Screen Time. ",
        ]}
      />

      <Insight
        insight={{
          body:
            "88% of users found info provided by the consumption and creations visualizations to be helpful",
        }}
        index={0}
      />

      <SolutionHeader
        title="Settings"
        description="To allow for more customization, we implemented two settings features that allow users to control how misinformation is presented in their feeds and what resources our fact-check detail pages would provide them."
      />

      <ImageTextPair
        imgSrc={exceptions}
        title="Feed Credibility"
        body="These settings allow users to control the visibility of content containing misinformation on the platform. Here, users can also allow add Exceptions, or accounts to exempt from these settings."
        goofy={true}
      />
      <Insight
        insight={{
          body:
            "73% of participants chose to edit the visibility of misinformation in their feeds",
        }}
        index={1}
      />
    </Section>
  );
}
